<template>
    <div class="flex flex-column gap-3 mb-3">
        <div class="flex justify-content-between w-full gap-3" v-for="(item, index) in model" :key="item">
            <Dropdown class="w-5" :options="getAvailablePaymentMethods(item)" :disabled="item.dpmInvoice || isVoucherLine(item)" v-model="item.paymentMethodCode" optionValue="paymentMethodCode" optionLabel="paymentMethodName" @change="changePayMent(item)"> </Dropdown>
            <InputNumber class="w-7" v-model="item.amount" :min="0" :disabled="item.id || isVoucherLine(item)"></InputNumber>
            <div class="w-1 flex">
                <Button icon="pi pi-pencil" text size="small" v-if="BANK_PAYMENT_CODES.includes(item.paymentMethodCode)" @click="openChangbank(index)"></Button>
                <Button v-if="!isVoucherLine(item)" icon="pi pi-trash" text size="small" severity="danger" @click="model = model.filter((e) => e != item)"></Button>
            </div>
        </div>
        <div>
            <Button v-if="model.length < 3" label="Thêm phương thức thanh toán" icon="pi pi-plus" text @click="addPaymentMethod"></Button>
        </div>
    </div>
    <Dialog v-model:visible="visiblePayMent" modal :header="`Danh sách ngân hàng`" :style="{ width: '85rem' }">
        <DataTable :value="dataBankisFilter?.filter((el) => el.status)" v-model:selection="selectedBanking" tableStyle="min-width: 80rem" resizableColumns columnResizeMode="fit">
            <template #empty>
                <span class="text-center p-5 w-full">Danh sách trống</span>
            </template>
            <Column selectionMode="single" headerStyle="width: 3rem"></Column>
            <Column field="code" header="QR CODE" style="width: 100px">
                <template #body="{ data }">
                    <img width="50" :src="data.image ? data.image : `https://placehold.co/600x800`" alt="" srcset="" />
                </template>
            </Column>

            <Column field="isDefault" header="Loại tài khoản">
                <template #body="{ data }"> {{ data.isDynamicQR ? 'QR động' : 'QR tĩnh' }}</template>
            </Column>
            <Column field="isOutGoing" header="Tài khoản chi">
                <template #body="{ data }"> {{ data.isOutGoing ? 'Có' : 'Không' }}</template>
            </Column>
            <Column field="cardholder" header="Tên tài khoản"></Column>
            <Column field="bankCardCode" header="Số tài khoản"></Column>
            <Column field="bankName" header="Tên ngân hàng">
                <template #body="{ data }">
                    <span>{{ data.bankName }} <Tag value="Mặc định" v-if="data.isDefault"></Tag></span>
                </template>
            </Column>
        </DataTable>
        <template #footer>
            <div class="flex justify-content-end gap-2">
                <Button type="button" label="Bỏ qua" severity="secondary" @click="visiblePayMent = false"></Button>
                <Button type="button" label="Xác nhận" @click="SaveBanking()"></Button>
            </div>
        </template>
    </Dialog>
</template>

<script setup>
import { ref, onBeforeMount, computed, watch } from 'vue';
import API from '@/api/api-main';
import { transaction } from '@/stores/transaction';
import { BANK_PAYMENT_CODES, getActivePaymentMethods, isIncomingOnlyMethod } from '@/helper/paymentMethod';
import { de } from 'date-fns/locale';

const Transaction = transaction();
const user = JSON.parse(localStorage.getItem('user'))?.appUser || {};
const dataBank = ref([]);

// Chỉ là lựa chọn đang hiện trong popup "Danh sách ngân hàng" — KHÔNG dùng làm TK cho dòng mới.
// Dòng chuyển khoản mới luôn lấy TK mặc định của quầy (xem changePayMent).
const selectedBanking = ref(null);

const visiblePayMent = ref(false);
const activeIndex = ref(-1);
const model = defineModel('paymentMethod', {
    type: Array,
    default: []
});

const props = defineProps({
    dataInvoice: {
        type: Object,
        default: () => ({})
    },
    taxAmount: {
        type: Number,
        default: 1000000
    }
});

const dataPaymentMethod = ref([]);
const checkBankDefault = ref(null);
const payload = computed(() => Transaction.$state?.data[Transaction.$state?.active]?.payload);

// Chiều tiền của chứng từ đang mở: true = quầy CHI trả cho khách (mua vào của khách,
// đổi/trả hàng phải hoàn tiền). Dùng cho cả việc chỉ cho chọn tài khoản chi (isOutGoing)
// lẫn việc ẩn các PTTT chỉ dùng chiều thu (Voucher).
const isOutgoingPayment = computed(() => {
    const name = Transaction.$state?.data[Transaction.$state?.active]?.type;
    switch (name) {
        case 'MUA_HANG':
        case 'MUA_HANG_VTS':
        case 'DOI_HANG':
            return true;
        case 'DOI_TRA_HANG_VTS':
            return !(payload.value?.totalCustomer > 0);
        default:
            return false;
    }
});

const dataBankisFilter = computed(() => (isOutgoingPayment.value ? dataBank.value.filter((e) => e.isOutGoing === true) : dataBank.value));

onBeforeMount(async () => {
    await Promise.all([getBankWithCounter(), getAllPayment()]);
});

const getAllPayment = async () => {
    // Gọi lại mỗi lần mở popup ngân hàng — lỗi mạng thì giữ danh mục cũ, đừng làm
    // trống dropdown của dòng thanh toán đang nhập dở.
    const methods = await getActivePaymentMethods();
    if (methods.length) dataPaymentMethod.value = methods;
};

const addPaymentMethod = () => {
    const total = payload.value?.total || 0;
    const totalPayment = model.value.reduce((acc, item) => acc + (item.amount || 0), 0);
    // Gán lại mảng mới (thay vì push mutate tại chỗ) để tương thích với v-model trỏ vào computed
    // get/set (trường hợp cha lọc/ẩn bớt dòng trước khi bind) — push trực tiếp sẽ không lan truyền
    // về state gốc trong trường hợp đó.
    model.value = [
        ...model.value,
        {
            id: 0,
            fatherId: 0,
            paymentMethodCode: '',
            paymentMethodName: '',
            amount: total - totalPayment || 0
        }
    ];
};

const changePayMent = (item) => {
    const selectedPayment = dataPaymentMethod.value.find((e) => e.paymentMethodCode === item.paymentMethodCode);
    if (selectedPayment) {
        item.paymentMethodName = selectedPayment.paymentMethodName;
        // id của danh mục do DB sinh nên khác nhau giữa các môi trường — luôn lấy
        // theo danh mục vừa đọc về, không được gán số cứng.
        item.paymentMethodId = selectedPayment.id;
    }

    if (BANK_PAYMENT_CODES.includes(item.paymentMethodCode)) {
        // Luôn lấy TK mặc định của quầy. Bản cũ ưu tiên TK nhớ trong localStorage (TK chọn
        // lần trước, kể cả chỉ mở popup bút chì) nên quầy đặt mặc định A vẫn bung QR B.
        updateBankingInfo(item, getDefaultBank());
    } else if (item.bankId || item.bankCardCode) {
        // Đổi từ chuyển khoản/quẹt thẻ sang Tiền mặt hoặc Voucher: xoá thông tin ngân
        // hàng còn sót lại, các PTTT này không có tài khoản ngân hàng.
        updateBankingInfo(item, null);
    }
};

const getDefaultBank = () => {
    // return dataBank.value.find((e) => e.isDefault) || null;
    return checkBankDefault.value?.bankCard || null;
};

const updateBankingInfo = (item, bank) => {
    item.bankId = bank?.bankId || 0;
    item.bankCode = bank?.bankCode || '';
    item.bankName = bank?.bankName || '';
    item.bankCardCode = bank?.bankCardCode || '';
    item.cardholder = bank?.cardholder || '';
    item.image = bank?.image || '';
    item.isDynamicQR = bank?.isDynamicQR || false;
    item.pinBank = bank?.pinBank || '';
    item.isOutGoing = bank?.isOutGoing || false;
};

const getBankWithCounter = async () => {
    try {
        const { data } = await API.get(`Counter/${props.dataInvoice?.counterId || user.counterIds[0] || null}`);
        // Quầy chưa gán TK nào thì danh sách phải về rỗng, không được giữ TK cũ.
        const bankCounter = data?.data?.bankCounter || [];
        dataBank.value = bankCounter.map((e) => ({
            ...e.bankCard,
            default: e.default
        }));
        checkBankDefault.value = bankCounter.find((el) => el.default === true) || null;
    } catch (error) {
        console.error('Error fetching bank data:', error);
    }
};

const SaveBanking = () => {
    visiblePayMent.value = false;
    if (activeIndex.value !== -1) {
        updateBankingInfo(model.value[activeIndex.value], selectedBanking.value);
    }
    activeIndex.value = -1;
};

const openChangbank = async (index) => {
    await Promise.all([getBankWithCounter(), getAllPayment()]);
    activeIndex.value = index;
    // Popup phải chọn sẵn ĐÚNG tài khoản của dòng đang sửa, không phải tài khoản mặc
    // định của quầy — nếu không, mở ra rồi bấm "Xác nhận" mà không chọn lại sẽ âm thầm
    // đổi ngân hàng của dòng đó.
    const line = model.value[index];
    selectedBanking.value = dataBank.value.find((el) => el.bankCardCode === line?.bankCardCode && el.bankCode === line?.bankCode) || dataBank.value.find((el) => el.default) || null;
    visiblePayMent.value = true;
};

// Dòng "Voucher" do ô nhập mã voucher (VoucherBox) quản lý: số tiền = tổng giá trị voucher đã áp,
// không cho chọn / sửa / xoá tay (BR-17) — backend cũng đối chiếu với mã lúc thanh toán.
const isVoucherLine = (item) => item.paymentMethodCode === 'Voucher';

// Lọc danh sách phương thức thanh toán khả dụng cho mỗi dòng
const getAvailablePaymentMethods = (currentItem) => {
    if (!dataPaymentMethod.value) return [];

    return dataPaymentMethod.value.filter((paymentMethod) => {
        // Nếu là phương thức hiện tại đang được chọn, luôn hiển thị
        if (currentItem.paymentMethodCode === paymentMethod.paymentMethodCode) {
            return true;
        }

        // Voucher chỉ dùng để THU tiền của khách — không cho chọn ở luồng chi/hoàn tiền
        // (backend cũng chặn bằng 400, nhưng đừng để chọn xong mới báo lỗi).
        if (isOutgoingPayment.value && isIncomingOnlyMethod(paymentMethod.paymentMethodCode)) {
            return false;
        }
        // Voucher chỉ vào được qua ô nhập mã voucher.
        if (paymentMethod.paymentMethodCode === 'Voucher') return false;

        // Kiểm tra xem phương thức này đã được chọn ở dòng khác chưa (trừ các dòng invoice)
        const isAlreadySelected = model.value.some((item) => item.paymentMethodCode === paymentMethod.paymentMethodCode && !item.dpmInvoice);

        // Chỉ hiển thị nếu chưa được chọn
        return !isAlreadySelected;
    });
};

watch(
    () => props?.dataInvoice,
    async (newVal, oldVal) => {
        await getBankWithCounter();
    }
);
</script>

<style scoped>
@media only screen and (max-width: 640px) {
    ._responsive_btn {
        display: none;
    }

    ._responsive_btn_2 {
        display: block;
    }
}

/* Màn hình di động lớn / máy tính bảng nhỏ (641px - 1023px) */
@media only screen and (min-width: 641px) and (max-width: 1023px) {
    ._responsive_btn {
        display: none;
    }

    ._responsive_btn_2 {
        display: block;
    }
}

/* Máy tính bảng hoặc màn hình nhỏ (≥ 1024px) */
@media only screen and (min-width: 1024px) and (max-width: 1279px) {
    ._responsive_btn {
        display: none;
    }

    ._responsive_btn_2 {
        display: block;
    }
}

/* Màn hình laptop (≥ 1280px) */
@media only screen and (min-width: 1280px) and (max-width: 1535px) {
    ._responsive_btn {
        display: none;
    }

    ._responsive_btn_2 {
        display: block;
    }
}

/* Màn hình lớn (≥ 1536px) */
@media only screen and (min-width: 1536px) and (max-width: 1920px) {
    ._responsive_btn {
        display: block;
    }

    ._responsive_btn_2 {
        display: none;
    }
}

/* Màn hình rất lớn (≥ 1921px) */
@media only screen and (min-width: 1921px) and (max-width: 2560px) {
    ._responsive_btn {
        display: block;
    }

    ._responsive_btn_2 {
        display: none;
    }
}

/* Màn hình 4K (≥ 2561px) */
@media only screen and (min-width: 2561px) {
    ._responsive_btn {
        display: block;
    }

    ._responsive_btn_2 {
        display: none;
    }
}
</style>
